import { downloadBlob } from "owp.http";
import type { JSX } from "preact";
import { jsonParse } from "@gss/common/jsonParse";
import { Button } from "@gss/components/Button";
import { Help } from "@gss/components/icons";
import { InputCheckbox } from "@gss/components/InputCheckbox";
import { InputFile } from "@gss/components/InputFile";
import { InputNumber } from "@gss/components/InputNumber";
import { Label } from "@gss/components/Label";
import { useMessages } from "@gss/components/Messages";
import { Select } from "@gss/components/Select";
import { Table, TableHeader } from "@gss/components/Table";
import { usePatchState } from "@gss/components/usePatchState";
import { DAO } from "../DAO";
import type { RawExportInclude } from "../types";
import type { MultiSelectChild } from "../util/MultiSelect";
import { MultiSelect } from "../util/MultiSelect";
import type { AugmentedExportResultForm, Option } from "./export.types";

// Grouping disabled for now. SKR need to decide if this is needed.

interface Props {
    conf: AugmentedExportResultForm;
}

interface State {
    selectedColumns: Option[];
    // selectedColumnsForGrouping: Option[];
    selectedQuestions: Option[];
    selectedFreeTextQuestions: Option[];
    selectedDimensions: Option[];
    include: RawExportInclude;
    limit: number | undefined;
    // groupingLimit: number;
    orgIds: string[];
}

type SelectedField =
    | "selectedColumns"
    // | "selectedColumnsForGrouping"
    | "selectedQuestions"
    | "selectedFreeTextQuestions"
    | "selectedDimensions";

export function ExportRaw({ conf }: Props): JSX.Element {
    const [state, setState] = usePatchState<State>({
        selectedColumns: [],
        // selectedColumnsForGrouping: [],
        selectedQuestions: [],
        selectedFreeTextQuestions: [],
        selectedDimensions: [],
        include: "answered",
        limit: undefined,
        // groupingLimit: 3,
        orgIds: [],
    });
    const { addError } = useMessages();

    const selectAllChanged = (
        options: Option[],
        selectedField: SelectedField,
        checked: boolean,
    ) => {
        const newState = (() => {
            if (checked) {
                return {
                    [selectedField]: [...options],
                };
            }
            return {
                [selectedField]: [],
            };
        })();
        setState(newState);
    };

    const selectionChanged = (
        option: Option,
        selectedField: SelectedField,
        checked: boolean,
    ) => {
        const selected = state[selectedField];
        if (checked) {
            selected.push(option);
        } else {
            selected.splice(selected.indexOf(option), 1);
        }
        setState({
            [selectedField]: selected,
        });
    };

    const limitChanged = (value: number | undefined) => {
        if (value !== state.limit) {
            setState({ limit: value });
        }
    };

    const downloadCsv = () => {
        const columns = getOptionsValueInOrder(
            conf.columns,
            state.selectedColumns,
        );
        const questions = getOptionsValueInOrder(
            conf.questions,
            state.selectedQuestions,
        );
        const freeTextQuestions = getOptionsValueInOrder(
            conf.freeTextQuestions,
            state.selectedFreeTextQuestions,
        );
        const dimensions = getOptionsValueInOrder(
            conf.dimensions,
            state.selectedDimensions,
        );
        DAO.export
            .downloadRawResult({
                measurementName: conf.measurementName,
                periods: [conf.periods],
                columns,
                questions,
                freeTextQuestions,
                dimensions,
                include: state.include,
                limit: state.limit,
                orgIds: state.orgIds,
            })
            .catch(addError);
    };

    const downloadConf = () => {
        const data = JSON.stringify(state);
        const blob = new Blob([data], {
            type: "application/json;charset=utf-8",
        });
        downloadBlob(blob, "gss_download_raw_conf.json");
    };

    const uploadConf = (files: FileList | null) => {
        const file = files?.[0];

        if (file == null) {
            return;
        }

        void file.text().then((content) => {
            if (content === "") {
                return;
            }
            const state = jsonParse<State>(content);
            state.selectedColumns = findUpload(
                conf.columns,
                state.selectedColumns,
            );
            state.selectedQuestions = findUpload(
                conf.questions,
                state.selectedQuestions,
            );
            state.selectedFreeTextQuestions = findUpload(
                conf.freeTextQuestions,
                state.selectedFreeTextQuestions,
            );
            state.selectedDimensions = findUpload(
                conf.dimensions,
                state.selectedDimensions,
            );
            setState(state);
        });
    };

    const getTable = (
        title: string,
        header: string,
        selectedField: SelectedField,
        options: Option[],
        groupingField?: SelectedField,
    ) => {
        const selected = state[selectedField];
        const isAllSelected = selected.length === options.length;
        const headers: JSX.Element[] = [
            <th key="checked" style={{ width: "4rem" }}>
                <InputCheckbox
                    key="0"
                    checked={isAllSelected}
                    onChange={(checked) => {
                        selectAllChanged(options, selectedField, checked);
                        if (!checked && groupingField != null) {
                            selectAllChanged(options, groupingField, checked);
                        }
                    }}
                />
            </th>,
        ];
        if (groupingField != null) {
            headers.push(
                <th key="grouping">
                    <span title="De kolumner som används när vi räknar om vi har tillräckligt många patienter för att säkerställa anonymitet">
                        Gruppering
                        <Help className="ms-1" />
                    </span>
                </th>,
            );
        }
        headers.push(<th key={header}>{header}</th>);

        return (
            <div className="mt-3">
                <TableHeader>
                    {title}
                    &nbsp;
                    <small>
                        ({selected.length} / {options.length})
                    </small>
                </TableHeader>
                <Table striped headers={<tr>{headers}</tr>}>
                    {options.map((o) => {
                        const checked = selected.some((s) => s === o);
                        return (
                            <tr key={o.value}>
                                <td>
                                    <InputCheckbox
                                        checked={checked}
                                        onChange={(checked) => {
                                            selectionChanged(
                                                o,
                                                selectedField,
                                                checked,
                                            );
                                            if (
                                                !checked &&
                                                groupingField != null
                                            ) {
                                                selectionChanged(
                                                    o,
                                                    groupingField,
                                                    checked,
                                                );
                                            }
                                        }}
                                    />
                                </td>
                                {groupingField && (
                                    <td>
                                        <InputCheckbox
                                            disabled={!checked}
                                            checked={state[groupingField].some(
                                                (s) => s === o,
                                            )}
                                            onChange={(checked) =>
                                                selectionChanged(
                                                    o,
                                                    groupingField,
                                                    checked,
                                                )
                                            }
                                        />
                                    </td>
                                )}
                                <td>{o.name}</td>
                            </tr>
                        );
                    })}
                </Table>
            </div>
        );
    };

    const renderTables = () => {
        return (
            <>
                {getTable(
                    "Kolumner",
                    "Kolumntitel",
                    "selectedColumns",
                    conf.columns,
                    // "selectedColumnsForGrouping"
                )}
                {getTable(
                    "Dimensioner",
                    "Dimension",
                    "selectedDimensions",
                    conf.dimensions,
                )}
                {getTable(
                    "Frågor",
                    "Fråga",
                    "selectedQuestions",
                    conf.questions,
                )}
                {getTable(
                    "Fritetxfrågor",
                    "Fråga",
                    "selectedFreeTextQuestions",
                    conf.freeTextQuestions,
                )}
            </>
        );
    };

    const renderInvited = () => {
        return (
            <tr>
                <td>
                    <Label title="Välj om vi ska inkludera samtliga patienter i underlaget, eller bara de som fått en enkät, eller bara de som besvarat sin enkät.">
                        Inkludera patienter
                    </Label>
                </td>
                <td>
                    <Select
                        value={state.include}
                        onChange={(include) => setState({ include })}
                    >
                        {[
                            { value: "all", children: "Samtliga patienter" },
                            { value: "invited", children: "Endast inbjudna" },
                            { value: "answered", children: "Endast besvarade" },
                        ]}
                    </Select>
                </td>
            </tr>
        );
    };

    const renderOrgRoles = () => {
        const res: MultiSelectChild[] = conf.regions.map((r) => (
            <option key={r.value} value={r.value}>
                {r.name}
            </option>
        ));
        return (
            <tr>
                <td>
                    <Label title="Begränsad datauttag till följande regioner. Om inga är valda så inkluderas samtliga.">
                        Regioner
                    </Label>
                </td>
                <td>
                    <MultiSelect
                        placeholder="Samtliga regioner"
                        values={state.orgIds}
                        onChange={(value, checked) => {
                            const orgIds = checked
                                ? [...state.orgIds, value]
                                : state.orgIds.filter((r) => r !== value);
                            setState({ orgIds });
                        }}
                    >
                        {res}
                    </MultiSelect>
                </td>
            </tr>
        );
    };

    const renderLimit = () => {
        return (
            <tr>
                <td className="pe-3">
                    <Label title="Tom = ingen begränsning / returnera alla">
                        Begränsa antalet svar
                    </Label>
                </td>
                <td>
                    <InputNumber
                        isInteger
                        value={state.limit}
                        onChangeOpt={limitChanged}
                    />
                </td>
            </tr>
        );
    };

    // const renderGrouping = () => {
    //     const selected = state.selectedColumnsForGrouping.length
    //         ? state.selectedColumnsForGrouping.map((s) => `* ${s.name}`).join("\n")
    //         : "Ingen gruppering vald!";
    //     return (
    //         <tr>
    //             <td>
    //                 <Label
    //                     title={`Minsta antal tillåtna patienter för grupperingen. Om antalet patienter inom den valda grupperingen är färre än detta så utesluts dessa patienter från exporten.\n\n${selected}`}
    //                 >
    //                     Gruppering
    //                     <QuestionCircleFill className="ms-1" />
    //                 </Label>
    //             </td>
    //             <td>
    //                 <InputNumber
    //                     disabled={state.selectedColumnsForGrouping.length === 0}
    //                     isInteger={true}
    //                     value={state.groupingLimit}
    //                     onChange={(value) =>
    //                         setState({
    //                             groupingLimit: value as number
    //                         })
    //                     }
    //                 />
    //             </td>
    //         </tr>
    //     );
    // };

    function renderFooter(): JSX.Element {
        return (
            <div>
                <hr />
                <Button variant="primary" onClick={downloadCsv}>
                    Hämta CSV
                </Button>
                <div className="float-end">
                    <Button variant="secondary" onClick={downloadConf}>
                        Hämta konf
                    </Button>
                    <InputFile
                        className="ms-3 d-inline-block w-auto"
                        accept=".json"
                        onChange={uploadConf}
                    />
                </div>
            </div>
        );
    }

    return (
        <div>
            {renderTables()}

            <table>
                <tbody>
                    {renderInvited()}
                    {renderOrgRoles()}
                    {renderLimit()}
                    {/* {renderGrouping()} */}
                </tbody>
            </table>

            {renderFooter()}
        </div>
    );
}

function findUpload(options: Option[], selected: Option[]): Option[] {
    const result: Option[] = [];
    for (const sel of selected) {
        const found = options.find((o) => o.value === sel.value);
        if (found != null) {
            result.push(found);
        } else {
            console.warn(`Not found: ${sel.value}`);
            console.log(options);
            console.log(sel);
        }
    }
    return result;
}

function getOptionsValueInOrder(
    options: Option[],
    selected: Option[],
): string[] {
    const result: string[] = [];
    for (const option of options) {
        const found = selected.find((s) => s.value === option.value);
        if (found != null) {
            result.push(found.value);
        }
    }
    if (result.length !== selected.length) {
        for (const sel of selected) {
            if (!result.includes(sel.value)) {
                console.error(`Not found: ${sel.value}`);
            }
        }
        throw new Error("Couldn't find all selected options");
    }
    return result;
}
